import {
  ACCOUNT_FEATURE_FLAGS,
  type CompanyView,
  FEATURE_FLAGS,
} from "@/constants";
import type { CancellationMode } from "@/dashboard/inventory_management/bulk_buy/bulkBuyTypes";
import type { Currency } from "@/money/currency_constants";
import type { ForwardDeliveryHeader } from "@/forwards2/forwardsTypes";
import type { RegistrationAuthority } from "@/utils/registryAccounts";
import type { SeApplicationInformation } from "@/signup/se/types";

// Derive the FeatureFlags type from the FEATURE_FLAGS constant
// This ensures we only maintain the list in one place
export type FeatureFlags = {
  [K in keyof typeof FEATURE_FLAGS]?: boolean;
};

// Derive the AccountFeatureFlags type from the ACCOUNT_FEATURE_FLAGS constant
export type { CompanyView };

export type AccountFeatureFlags = {
  [K in keyof typeof ACCOUNT_FEATURE_FLAGS]?: boolean;
};

export interface Account {
  id?: number;
  email: string | null;
  name: string | null;
  registry_code: string | null;
  phone_number: string | null;
  has_registered: boolean;
  session_id: string | null;
  kilowatts: number;
  go_price: number;
  companies: Company[];
  internal_role?: string | null;
  /** Absent only on the placeholder account the store holds before the server answers. */
  language?: string;
  /**
   * The internal admin driving the request — the impersonating admin, or the caller themselves when
   * staff are working as themselves. Null for a client caller, so it is the admin-session flag the
   * admin-only surfaces render behind. Describes the session, not the account `internal_role` does.
   */
  admin_account_id?: number | null;
  /**
   * That admin's own role, null alongside a null `admin_account_id`. While an admin manages a client
   * account, `internal_role` above is the MANAGED account's (`user`) but the request's access
   * ceiling comes from the admin — so anything gating on staff rights reads the effective role
   * (`admin_internal_role ?? internal_role`), never `internal_role` alone.
   */
  admin_internal_role?: string | null;
  conversion_tracking?: ConversionTracking;
  conversion_tracking_id?: number | null;
  /** Null unless an admin is driving the request. */
  trackings: ConversionTracking[] | null;
  comment?: string | null;
  total_devices_power?: number;
  feature_flags?: AccountFeatureFlags;
  /** True when the account has opted out of sale notification emails (account copy only). */
  transaction_emails_unsubscribed?: boolean;
  managing_account?: string | null;
  managing_account_id?: number | null;
}

/** What an endpoint that changes the account returns: `AccountUpdateResult` in app/account/account_update_service.py. */
export interface AccountUpdateResult {
  accountData: Account;
  company_id: number | null;
  generator_id?: number;
}

/** An account change that saved a company: a signup step's save, or the corporate signup's create. */
export interface CompanySaveResult extends AccountUpdateResult {
  company_id: number;
}

/** What a signup step's generator file upload returns, with the generator it filed the file under. */
export interface GeneratorUploadResult extends CompanySaveResult {
  generator_id: number;
}

/** Whom the session manages: `ManagingAccountResponse` in app/services/admin/account_managing_service.py. */
export interface ManagingAccountResponse {
  managing_account: string | null;
  managing_account_id: number | null;
}

export interface ConversionTracking {
  id: number;
  referrer_id?: number;
  utm_source?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_content?: string;
  utm_campaign?: string;
  created_at: string;
}

/** Today's ECB reference rate for a company paid out in another currency: units of that currency per 1 EUR. */
export interface IndicativePayoutRate {
  rate: string;
  rate_date: string;
}

export interface Company {
  id?: number;
  name: string;
  address: string | null;
  registry_code: string | null;
  has_registered: boolean;
  generators: Generator[];
  documents: Document[];
  registry_devices: RegistryDevice[];
  registry_connections: RegistryConnection[];

  vat: string | null;
  bank_account: string | null;
  bank_name: string | null;
  bank_swift: string | null;
  payout_currency?: Currency;
  indicative_payout_rate?: IndicativePayoutRate | null;
  payment_header: string;
  fee_model: string | null;
  registry_excluded_devices: string[];
  unlocked_hosted_account_regions?: string[];
  /** Admin-set allowlist of email domains team invites may go to; empty means any domain. */
  allowed_invite_domains?: string[];
  country: string;
  contact_email?: string | null;
  invoice_email?: string | null;
  is_test_company: boolean;
  nda_signed_link?: string;
  legal_keys: string[];
  /** Picks the dashboard; see COMPANY_VIEWS. */
  dashboard_view: CompanyView;
  feature_flags?: FeatureFlags;
  is_trusted?: boolean;
  /** The company still owes its plan: the app shows a mock dashboard behind the billing prompt. */
  payment_required?: boolean;
  total_devices_power?: number;
  /** True when the company has any live registry device. Every account-tree response sends it; the admin accounts list sends `registry_devices` instead. */
  has_registry_devices?: boolean;
  deleted_at?: string | null;
  membership_agreement_signed_at?: string | null;
  contract_version?: "v4" | "v5" | null;
  /** True when the company's sale notifications are skipped; it gets only the month-end email with the fee invoice on the 1st. */
  monthly_notification_rollup?: boolean;
  /** True when an admin silenced the company: the staff retention digest skips it and it gets no "allocation ready" emails. */
  retention_notification_silenced?: boolean;
}

export interface Generator {
  id?: number;
  company_id?: number;
  grid_contract: string | null;
  solar_panel_model: string | null;
  has_subsidy: boolean | null;
  has_registered: boolean;
  device_eic: string | null;
  device_address: string | null;
  device_power: number | null;
  device_name: string | null;
  device_country: string | null;
  device_technology: string | null;
  grid_contract_start_date: string | null;
  license_number: string | null;
  // Only the LV signup fills the fields below, and holds them null while their input is cleared.
  lat?: number | null;
  lng?: number | null;
  only_device_output?: boolean | null;
  device_fuel?: string;
  device_grid_operator?: "TSO" | "DSO" | null;
  device_power_in_permit?: number | null;
  /** Uploaded file URIs keyed by document kind (LT signup). */
  files: Record<string, string> | null;
  /** The SE signup's form; no other flow reads or writes it. */
  application_information?: SeApplicationInformation;

  subsidies: Subsidy[];

  battery: Battery | null;
  ppa: Ppa | null;
}

export interface Subsidy {
  id?: number;
  generator_id?: number;
  provider: string;
  decision_date: string | null;
  plan: string | null;
  amount: number | null;
  number?: string;
  objective?: string;
  subsidy_type?: string;
}

export interface Document {
  id?: number;
  company_id?: number;
  generator_id?: number;
  dokobit_token: string;
  name: string | null;
  signing_url: string | null;
  download_url: string;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
  has_grid_contract: boolean;
  has_authorization_contract: boolean;
  has_soldera_contract: boolean;
  document_signers: DocumentSigner[];
  contract_keys: string[];
  file_metadata?: FileMetadataMap | null;
  legal_keys: string[];
}

export interface DocumentSigner {
  id: number;
  document_id: number;
  account_id: number;
  role: string;
  token: string;
  redirect_url: string;
}

// Add the Contract type here
export type Contract = {
  contract_key: string;
  meta: {
    title: string; // i18n key
    description: string; // i18n key
  };
  include: boolean; // Whether it's currently selected (relevant for admins)
  can_include: boolean; // Whether it *can* be included/excluded by admin
};

export interface Step {
  title: string;
  isAvailable: boolean;
}

export interface Battery {
  id?: number;
  generator_id: number;
  kw: number | null;
  kwh: number | null;
}

export interface Ppa {
  id?: number;
  generator_id: number;

  start_date: string | null;
  mwh: number | null;
  client_company_name: string | null;
  country: string | null;
}

/**
 * How a month-aligned payout request divides the pending items between the previous month's
 * back-dated invoice and a second invoice dated today.
 */
export interface MonthSplit {
  /** Last day of the previous month: the date the previous month's invoice carries. */
  invoice_date: string;
  previous_month_item_ids: number[];
  current_month_item_ids: number[];
  creates_second_invoice: boolean;
  default_current_month_invoice_number: string;
}

/**
 * A company's "Money pending" figure as the backend computes it, with the plan a payout requested now
 * posts: what that payout would pay, in the company's payout currency, positive when owed to it.
 *
 * @property amount - A decimal string, rounded to the currency's minor unit
 * @property converted - Part of it converts at today's ECB rate, so a payout requested later may pay
 *   another figure; a fee a live fee invoice already converted keeps that invoice's figure
 */
export interface PendingPayout {
  amount: string;
  currency: Currency;
  converted: boolean;
}

/** Each company's pending payout by company id; null where the backend had no recent ECB rate to compute it. */
export type PendingPayoutByCompanyId = Record<number, PendingPayout | null>;

export interface InvoiceItem {
  id: number;
  created_at: string;
  updated_at: string;

  company_id: number;
  company_name: string | null;
  description: string;
  amount: number;
  unit_price: string;
  vat_fraction: number | null;
  expense_requested_at: string | null;
  expense_paid_at: string | null;
  expense_canceled_at: string | null;

  expense_invoice_id: number | null;
  fee_invoice_id: number | null;
  item_type: string;
  /** The currency of gross/vat/net_units and the display figures: the line's document currency. */
  currency: Currency;
  /** Exact line value in ledger units (10⁻⁷ of currency); sum them, then convert with `fromUnits`. */
  gross_units: number;
  vat_units: number;
  net_units: number;
  display_unit_price: string;
  display_total_excl_vat: string;
  display_vat_amount: string;
}

export interface Invoice {
  id: number;
  created_at: string;
  updated_at: string;
  invoice_type: string;
  company_id: number;
  vat_number: string | null;
  bank_account: string | null;
  payment_header: string;
  invoice_number: string;
  requested_at: string | null;
  paid_at: string | null;
  canceled_at: string | null;
  period_start: string | null;
  period_end: string | null;
  merit_metadata: Record<string, any>;
  expense_items: InvoiceItem[];
  fee_items: InvoiceItem[];
  display_amount: number;
  currency: string;
  is_settlement_record?: boolean;
  is_fee_only?: boolean;
}

export interface ScheduledEmail {
  id: number;
  job_id: string;
  created_at: string | null;
  updated_at: string | null;
  scheduled_at: string;
  sent_at: string | null;
  status: string;
  account_id: number;
  company_id: number | null;
  email_type: string;
  email_metadata: Record<string, unknown> | null;
  error_message: string | null;
}

export interface UpsertRegistryDeviceRequest {
  device_eic: string;
  device_name: string;
  device_power: number;
  tge_technology_code: string;
  commercial_operation_date?: string | null;
  support?: boolean | null;
  ecolabel_approved?: boolean | null;
  country_specific_metadata?: Record<string, unknown> | null;
}

export interface RegistryDevice {
  id: number;
  company_id: number;
  registry_integration_name?: string | null;
  device_eic: string;
  device_name: string;
  device_power: number;
  support: string;
  certificates: Certificate[];
  registry_status: string;
  technology_code: string;
  technology: string;
  commercial_operation_date: string | null;
  ecolabel_approved: boolean | null;
  address: string | null;
  country_specific_metadata?: Record<string, unknown> | null;
  production_estimate_override?: {
    format: number;
    monthly_estimate_mwh: number[];
    updated_at: string;
  } | null;
  // True when the registry has standing (recurring) scheduled transactions sourcing from this device.
  has_scheduled_transactions?: boolean;
  /** The company's own device group the device is in (device_groups/), if any. */
  device_group_id: number | null;
  device_group_name: string | null;
}

export interface Certificate {
  id: number;
  transfer_deadline: string;
  transfer_deadline_passed: boolean; // Computed server-side: transfer_deadline < now()
  // Nullable: CNMC import-sourced holdings carry no device metadata at the source.
  production_device_name: string | null;
  production_support: boolean | null;
  fuel_code: string | null;
  count: number;
  sales_transaction_item_id: number | null;
  production_month: string;
  registry_status: string;
  transferred_at: string | null;
  technology_code: string;
  technology: string;
}

export interface SalesTransactionItem {
  id: number;
  company_id: number;
  company_name: string;
  amount: number;
  unit_price: string | null;
}

export interface SalesTransactionGlobalCompany {
  id: number;
  vat: string | null;
  business_id: string;
  address: string | null;
  emails: string | null;
}

// Narrow contract emitted by GlobalCompanySearchDropdown — adding fields is a deliberate API change here.
// `id`, `name`, and `country` are guaranteed by the search API; the remaining fields may be missing.
export interface PickedCompany {
  id: number;
  name: string;
  country: string;
  account_eecs: string | null;
  emails: string | null;
}

export interface SalesTransaction {
  id: number;
  transaction_at: string | null;
  unit_price: string | null;
  /** The deal currency, which unit_price, min_price, total_price and the item prices are in. */
  currency: Currency;
  /** False once the deal is a forward delivery, completed, or has anything posted in its currency. */
  currency_editable: boolean;
  amount: number;
  status:
    | "DRAFT"
    | "PENDING_BUYER_DETAILS"
    | "PENDING_CONTRACTS"
    | "READY_TO_TRANSFER"
    | "DELIVERED"
    | "COMPLETED";
  description: string | null;
  /** Internal admin notes; never shown to producers or buyers */
  internal_notes: string | null;
  counterparty_name: string | null;
  counterparty_country: string | null;
  counterparty_registry_country: string | null;
  counterparty_eecs_account_number: string | null;
  counterparty_registration_authority: "AIB" | "REGO" | "RGP" | null;
  registration_authority_data: { unit_price_pln?: string } | null;
  counterparty_emails: string | null;
  min_price: string | null;
  transaction_link: string | null;
  contract_links: string | null;
  offer_proof_links: string | null;
  volumes_specification_links: string | null;
  total_price: string | null;
  // Present on the admin list endpoint only: fees incl. VAT per currency as charged, the deal currency first
  total_fees_charged?: { currency: Currency; amount: string }[] | null;
  exclude_referral_commission?: boolean;
  certificate_ids?: number[];
  buyer_sales_invoice_id: number | null;
  buyer_sales_invoice_number: string | null;
  buyer_sales_invoice_paid_at: string | null;
  buyer_sales_invoice_due_at: string | null;
  buyer_sales_invoice_is_overdue: boolean;
  buyer_sales_invoice_merit_registered: boolean;
  buyer_sales_invoice_email_sent: boolean;
  buyer_sales_invoice_sent_at: string | null;
  buyer_sales_invoice_sent_manually: boolean;
  // ISO timestamps of automatic payment reminders emailed to the buyer, oldest first
  buyer_sales_invoice_reminders_sent_at: string[];
  items: Record<number, SalesTransactionItem> | null;
  global_company_id: number | null;
  global_company: SalesTransactionGlobalCompany | null;
  swap_referrer_company_id: number | null;
  /**
   * The forwards v2 delivery this transaction ships; absent on a spot sale.
   *
   * When it is present the deal owns the items, amounts, buyer details and prices — the
   * transaction page shows them read-only and links to the deal.
   */
  forward_delivery?: ForwardDeliveryHeader | null;
}

/** One row of the admin buyer-invoice audit trail; mirrors BuyerInvoiceHistoryEvent. */
export interface BuyerInvoiceHistoryEvent {
  occurred_at: string;
  event: string;
  description: string;
  invoice_id: number | null;
  invoice_number: string | null;
  actor: string;
}

export interface BuyerInvoiceHistoryResponse {
  sales_transaction_id: number;
  events: BuyerInvoiceHistoryEvent[];
}

export interface SalesTransactionCompanyBreakdownItem {
  sales_transaction_item_id: number;
  company_id: number;
  company_name: string;
  amount: number;
  effective_unit_price: string | null;
  revenue: string | null;
  soldera_fees: string | null;
  registry_fees: string | null;
  fees_pending: boolean;
}

export interface MultiCompanySalesTransaction {
  id: number;
  transaction_at: string | null;
  /** The deal currency: prices, revenue and Soldera fees are in it; a producer's registry fees are EUR. */
  currency: Currency;
  status: SalesTransaction["status"];
  description: string | null;
  counterparty_country: string | null;
  min_price: string | null;
  transaction_link: string | null;
  total_amount: number;
  price_min: string | null;
  price_max: string | null;
  total_revenue: string | null;
  total_soldera_fees: string | null;
  total_registry_fees: string | null;
  fees_pending: boolean;
  items: SalesTransactionCompanyBreakdownItem[];
}

export interface SalesTransactionItemDeviceVolume {
  sales_transaction_item_id: number;
  production_device_name: string;
  amount: number;
  revenue: string | null;
  soldera_fees: string | null;
  registry_fees: string | null;
}

export interface PowerProjection {
  [month: number]: number;
}

export interface FileMetadataMap {
  [key: string]: FileMetadataItem;
}

export interface FileMetadataItem {
  contract_key: string;
  title: string;
  description: string;
  file_url: string;
  legal_key: string;
}

export type ManagerShareSource =
  "SALE_REFERRAL" | "SWAP_REFERRAL" | "FEE_REFERRAL";

/**
 * Managed partner: the company earning a share of this company's bonuses
 */
export interface ManagerShare {
  company_id: number;
  rate: number; // Manager's share of each bonus (0.0-1.0)
  applies_to: ManagerShareSource[]; // Bonus sources the share applies to
}

/**
 * Commission Configuration for sales partners and referrers
 *
 * Used in the sales commission dashboard and edit commission modal
 */
export interface CommissionConfig {
  rate: number; // Commission rate (0.0-1.0, e.g., 0.3 for 30%)
  duration_years: number | null; // Duration in years (null for indefinite)
  year_2_rate?: number; // Second year commission rate (0.0-1.0)
  permanent_rate?: number; // Rate applied after initial duration (0.0-1.0)
  special_price?: number; // Multiplier for special price calculations
  special_condition?: string; // Special conditions (e.g., "no_cash_payout")
  expiry_date?: string; // ISO date; hard end of the referral, overrides the duration window
  manager?: ManagerShare; // Absent when the company has no manager
}

// Date period representation
export interface DatePeriod {
  start_date: string; // ISO date string
  duration: string; // ISO 8601 duration string (e.g., "P1Y" for 1 year)
}

// Period limit for PPA portfolios
export interface PeriodLimit {
  period: DatePeriod;
  limit_mwh: number; // Non-negative integer
  already_executed?: number; // Amount already executed (transferred/cancelled) for this period
}

export type PortfolioType =
  | "UNALLOCATED"
  | "FORWARD"
  | "HOLD"
  | "SPOT_MARKET_PRICE"
  | "SPOT_STX"
  | "SPOT_MIN_PRICE"
  | "PPA"
  | "OWN_USE";

// Portfolio instructions configuration
export interface PortfolioInstructions {
  allocate_percentage: number; // 0-100, default to 0
  limit_price: string | null; // Decimal string with up to 3 decimal places, can be null
  duration_from_issuance: string | null; // ISO 8601 duration string (e.g., "P1Y" for 1 year), can be null
  registry_device_ids: string[]; // List of registry device IDs, defaults to empty array
  limits: PeriodLimit[]; // List of period limits for PPA portfolios, defaults to empty array
}

// Main Portfolio interface
export interface Portfolio {
  id?: number;
  name: string;
  company_id: number;
  portfolio_type: PortfolioType;
  is_default: boolean;
  portfolio_instructions: PortfolioInstructions | null;
  custom_user_upload_id: number | null; // legacy field
  action_instructions?: ActionInstructions;
}

// Portfolio with guaranteed instructions (eliminates need for null checks)
export type PortfolioWithInstructions = Portfolio & {
  portfolio_instructions: PortfolioInstructions;
};

export interface PortfolioItem {
  id: number;
  count: number;
  certificate: Certificate | null;
}

export interface FlattenedPortfolioItem {
  // Portfolio Item fields
  id: number; // portfolio_item_id
  count: number;
  certificate_count?: number;
  /** The part of `count` the context selected or covers; equal to `count` when whole. */
  selected_count?: number;
  /** Kept after the order finishes, as provenance: the order's item list reads it. */
  company_order_id: number | null;
  /** Whether a still-running order holds the item; a finished order's link reserves nothing. */
  reserved_by_active_order: boolean;
  updated_at: string; // Representing datetime as string

  // Portfolio fields
  portfolio_id: number;
  portfolio_name: string;
  portfolio_type: string;
  is_static_type: boolean;
  is_default: boolean;

  // Company field
  company_id: number;
  company_name: string;
  account_emails: string[];

  // Certificate fields
  certificate_id: number;
  registry_id?: string | null;
  bundle_start?: string | null;
  bundle_end?: string | null;
  transfer_deadline: string | null; // Representing datetime as string
  transfer_deadline_passed: boolean; // Computed server-side: transfer_deadline < now()
  sales_transaction_item_id: number | null;
  production_device_name: string | null;
  /** The device's own company group (device_groups/), if any. */
  device_group_id: number | null;
  device_group_name: string | null;
  fuel_code: string | null;
  technology_code: string | null;
  technology_type: string | null;
  production_support: boolean | null;
  ecolabel_approved: boolean;
  registry_status: string | null;
  transferred_at: string | null; // Representing datetime as string
  production_month: string; // Format: YYYY-MM
  registry_region: string; // Registry region for instant client-side mismatch warning
  issuing_region: string;
  registration_authority?: RegistrationAuthority;

  limit_price: string | null;
  sale_price: string | null;
  /** The deal currency `sale_price` is in; null whenever `sale_price` is */
  sale_currency: Currency | null;

  // Calculated fields
  status: string;
  cod_age: number | null;
  okpower_new_compatible: boolean;
  count_for_transfer?: number; // Dynamically added by PortfolioItemsTable when enableCountEditing is true
}

/**
 * What a PortfolioItemsTable's filters leave visible.
 *
 * @property availableMwh - MWh of the visible rows the table's selection policy allows selecting
 * @property items - The visible rows in no particular order: the array is kept while only their
 *   order changes, so read the table's `getFilteredItems()` where the displayed order matters
 */
export interface FilteredPortfolioStats {
  count: number;
  totalMwh: number;
  availableMwh: number;
  items: FlattenedPortfolioItem[];
}

/**
 * Outcome of a portfolio move, as returned by the backend's `MoveResult`.
 *
 * @property moved - Items repointed to the destination portfolio
 * @property transferred - Of those, the ones that also moved between registry accounts (0 for a DB-only move)
 */
export interface MoveResult {
  moved: number;
  transferred: number;
}

// --- GO Cancel Feature Types ---
export enum BeneficiaryTypeEnum {
  ELECTRICITY_SELLER = "ELECTRICITY_SELLER",
  CONSUMER = "CONSUMER",
  CONVERSION_PLANT_OPERATOR = "CONVERSION_PLANT_OPERATOR",
}

/**
 * Registry-specific cancellation options (DEV-583), namespaced per registry family. Honored only by
 * registries on the Grexel platform (the registry options API's `supports_private_statement`); other
 * payload builders ignore the object.
 *
 * A public statement carries no options object at all, matching the backend default — so read the
 * privacy state with `isStatementPrivate` and write it with `withStatementPrivacy`, never by hand.
 *
 * @property is_public_cancellation - Whether the registry publishes an openly accessible URL for the
 * cancellation-statement PDF.
 */
export interface GrexelStatementOptions {
  is_public_cancellation: boolean;
}

export const TGE_LEGAL_FORMS = [
  "limited_company",
  "partnership",
  "private_partnership",
  "sole_proprietorship",
  "other",
] as const;

export type TgeLegalForm = (typeof TGE_LEGAL_FORMS)[number];

/**
 * End-user details the TGE (Poland) redemption form asks for, honored only by TGE (the registry
 * options API's `uses_tge_options`). Both are printed on the TGE cancellation statement.
 *
 * @property legal_form - Null when the backend reads it from the legal-form designation in the
 * beneficiary name; a statement whose name carries none is rejected.
 * @property vat_number - The NIP, with or without the country prefix. Distinct from `registry_code` (KRS).
 */
export interface TgeStatementOptions {
  legal_form: TgeLegalForm | null;
  vat_number: string | null;
}

export interface IndividualCancelStatement {
  // Unique temporary ID for v-for key, not sent to backend
  temp_id?: string;

  beneficiary_name: string;
  beneficiary_type: BeneficiaryTypeEnum;
  registry_code?: string | null; // Unified field for company or personal ID
  registry_country_region?: string; // Optional in PPA flow; registry derived from selected devices
  address?: string | null;
  period_start?: string; // Date string YYYY-MM-DD (optional for automated PPA cancellations)
  period_end?: string; // Date string YYYY-MM-DD (optional for automated PPA cancellations)
  cancellation_purpose: string;
  mwh_cancelled: number | null; // Standardized to match database field name
  is_any_aib?: boolean;
  grexel_options?: GrexelStatementOptions | null;
  tge_options?: TgeStatementOptions | null;
  isCollapsed?: boolean; // For UI state, optional
}

export interface PortfolioItemTransfer {
  id: number; // PortfolioItem ID
  count: number; // Total MWh count available for this item (immutable)
  selected_count: number; // MWh count selected from this item for this specific order
}

export interface GoCancelOrderRequest {
  statements: IndividualCancelStatement[];
  portfolio_items: PortfolioItemTransfer[];
  global_operation_notes?: string | null;
  has_region_mismatch?: boolean; // Indicates if selected GOs are from different regions than selected registry
  /** How to cancel the statements whose region Soldera can't cancel in directly; the backend defaults to the agent route. */
  cancellation_mode?: CancellationMode | null;
}

// --- End GO Cancel Feature Types ---

// --- PPA Action Instructions Types ---
export enum DeliveryMethod {
  AUTOMATIC = "AUTOMATIC",
  MANUAL = "MANUAL",
}

export enum DeliveryTiming {
  YEARLY = "YEARLY",
  HALF_YEAR = "HALF_YEAR",
  QUARTERLY = "QUARTERLY",
  MONTHLY = "MONTHLY",
  FIXED_DATES = "FIXED_DATES",
}

export enum PpaActionTransactionType {
  SEND = "SEND",
  CANCEL = "CANCEL",
}

/**
 * PPA SEND recipient: either a hosted-account company (`recipient_company_id` set, no registry
 * fields) or an external registry account (country plus EECS account, or a CNMC R2 code). The
 * backend omits the keys a shape doesn't use; sending them as null is accepted.
 */
export interface ActionSendTemplate {
  recipient_cnmc_r2?: string | null;
  recipient_company_id?: number | null;
  recipient_company_name: string;
  recipient_registry_country?: string | null;
  recipient_eecs_account_number?: string | null;
  recipient_registry_internal_account_number?: string | null;
}

export interface RecurringDateSpec {
  day: number; // 1-31
  month: number; // 1-12
}

export interface ActionInstructions {
  delivery_method: DeliveryMethod;
  delivery_timing: DeliveryTiming;

  recurring_date_specs: RecurringDateSpec[];

  start_date?: string | null; // ISO date string; required for monthly/quarterly allocation
  // End date is now always required
  end_date: string; // ISO date string (required for all timing types)

  transaction_type: PpaActionTransactionType;
  send_template?: ActionSendTemplate | null;
  cancel_statement?: IndividualCancelStatement | null;
  custom_user_upload_ids: number[];
  notes?: string;
}

/**
 * Partial payload for analyze-upload and patch updates
 *
 * AI analysis may only extract some fields from uploaded documents,
 * so all fields are optional to allow partial updates to ActionInstructions.
 */
export type ActionInstructionsPatch = Partial<{
  delivery_method: DeliveryMethod;
  delivery_timing: DeliveryTiming;
  recurring_date_specs: RecurringDateSpec[];
  start_date: string | null;
  end_date: string;
  transaction_type: PpaActionTransactionType;
  send_template: ActionSendTemplate | null;
  cancel_statement: IndividualCancelStatement | null;
  custom_user_upload_ids: number[];
  notes: string;
}>;

/**
 * Granularity for PPA portfolio fixed-amount allocation.
 */
export type LimitGranularity = "yearly" | "quarterly" | "monthly";

/**
 * Patch payload for portfolio_instructions extracted by AI (PPA only).
 * Either `limits` is fully expanded, or `reference_year_values` is supplied
 * (and the frontend expands it using the contract range).
 */
export interface PortfolioInstructionsPatch {
  granularity?: LimitGranularity;
  limits?: PeriodLimit[];
  reference_year_values?: number[];
  leap_feb_override?: number;
}

/**
 * Response from PPA contract analysis endpoint
 *
 * @throws {Error} When file upload fails or AI analysis encounters an error
 */
export interface PpaAnalyzeUploadResponse {
  instructions_patch: ActionInstructionsPatch;
  portfolio_instructions_patch?: PortfolioInstructionsPatch;
  message: string;
}
// --- End PPA Action Instructions Types ---

export type RegistrySyncTier = "NIGHTLY" | "FREQUENT";

export type TransactionLogRuleExclusion =
  | "REGISTRY_UNSUPPORTED"
  | "HOSTED_ACCOUNT"
  | "DEVICE_TRANSFER"
  | "SOLDERA_OWN_ACCOUNT";

export interface RegistryConnection {
  id: number;
  integration_name: string;
  registry_company_id: string | null;
  registry_eecs: string | null;
  region: string;
  // Regions the connection can be moved to; more than one only for a multi-country registry such as Grexel
  region_options: string[];
  connection_type: "STANDARD" | "VIRTUAL";
  eligible_for_issuance_fee: boolean;
  eligible_for_soldera_issuance_fee: boolean;
  transaction_log_disabled: boolean;
  transaction_log_rule_exclusion: TransactionLogRuleExclusion | null;
  sync_tier: RegistrySyncTier;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  // CNMC (Spain) only: redemption trámite registro numbers reported by the client —
  // the only way their redemptions become visible to the sync reconstruction.
  known_redemption_registros?: string[];
  spanish_issuance_enabled?: boolean;
}

export interface DeviceMuteInfo {
  muted_at: string;
  muted_until: string;
  muted_reason: string | null;
  muted_by: string;
}

export type DeviceMuteStatus = "active" | "expired" | "unmuted";

export interface DeviceMuteHistoryEntry extends DeviceMuteInfo {
  id: number;
  // "unmuted" covers both an explicit unmute and a mute superseded by a newer one.
  status: DeviceMuteStatus;
  unmuted_at: string | null;
  unmuted_by: string | null;
}

export interface DeviceMuteHistoryResponse {
  active: DeviceMuteHistoryEntry | null;
  // Newest first, capped server-side; includes the active entry when there is one.
  history: DeviceMuteHistoryEntry[];
}

export interface IssuanceDashboardDevice {
  id: number;
  name: string | null;
  expected_mwh: number;
  issued_mwh: number;
  mute: DeviceMuteInfo | null;
  // Month-data classification: "empty" (0/0 — dropped from everything, shipped so
  // operators can still see/mute the row), "small" (counted in MWh, excluded from
  // count-based metrics), "scored" (drives counts and percentages). Mute is shipped
  // separately and overlaid client-side (see deviceTier in issuanceMetrics.ts).
  stat_tier: "empty" | "small" | "scored";
  // ISO timestamp of when the device was added to our system; drives the "new" pill.
  created_at: string;
  // True when the registry has standing (recurring) scheduled transactions sourcing from this
  // device; drives the automatic-transfer indicator icon next to the device name.
  has_scheduled_transactions: boolean;
}

export interface IssuanceDashboardCompany {
  id: number;
  name: string | null;
  country: string | null;
  registry: string;
  // Name of the referrer/sales-partner company, if any.
  referrer_company_name: string | null;
  devices: IssuanceDashboardDevice[];
}

export interface IssuanceDashboardResponse {
  year: number;
  month: number;
  companies: IssuanceDashboardCompany[];
}

/**
 * - scored: coloured on a red-to-green gradient by issued_mwh / expected_or_issued_mwh
 * - pending: nothing issued yet and the issuance deadline has not passed
 * - empty: no scored devices in the cell
 */
export type IssuanceBirdseyeStatus = "scored" | "pending" | "empty";

/**
 * @property devices_total - Scored (non-small) devices; a cell without any is "empty"
 * @property small_total - Small devices; kept out of the device counts, included in the MWh
 * @property issued_mwh - Issued MWh of scored and small devices
 * @property expected_or_issued_mwh - Per device, its issued MWh once any is issued, else its projection; summed over scored and small devices
 */
export interface IssuanceBirdseyeCell {
  year: number;
  month: number;
  status: IssuanceBirdseyeStatus;
  devices_total: number;
  devices_issued: number;
  small_total: number;
  small_issued: number;
  issued_mwh: number;
  expected_or_issued_mwh: number;
  deadline: string;
  deadline_passed: boolean;
}

export interface IssuanceBirdseyeRow {
  country: string;
  registry: string | null;
  cells: IssuanceBirdseyeCell[];
}

export interface IssuanceBirdseyeMonth {
  year: number;
  month: number;
}

export interface IssuanceBirdseyeResponse {
  months: IssuanceBirdseyeMonth[];
  rows: IssuanceBirdseyeRow[];
}

export type BookedCommissionStatus = "pending" | "requested" | "paid";

/**
 * The currency of a deal an EUR admin report converted, with the ECB rate it was converted at.
 * Mirrors `DealEurRate` as the report rows serialize it.
 *
 * @property eur_rate - Units of `currency` per 1 EUR, as the backend prints it; null for an EUR deal
 * @property eur_rate_indicative - True while the deal has no buyer invoice, so the rate is today's
 */
export interface DealEurConversion {
  currency: Currency;
  eur_rate: string | null;
  eur_rate_indicative: boolean;
}

/** One referral line; every money figure is in EUR (see `DealEurConversion`). */
export interface BookedCommissionDeal extends DealEurConversion {
  invoice_item_id: number;
  sales_transaction_id: number | null;
  source_fee_type: string | null;
  created_at: string | null;
  sale_date: string | null;
  volume_mwh: number;
  effective_price: number | null;
  /** Swap deals: the sale's margin. Standard referrals: Soldera's commission fee on the sale. */
  soldera_fee_total: number | null;
  company_fee_rate: number | null;
  referral_rate: number | null;
  tier_label: string;
  commission_total: number;
  /**
   * What the buyer pays for the sale (a fee-based bonus: the fee), as on the financial dashboard.
   * Null for manager-share and COGS lines, which only adjust a bonus.
   */
  gross_revenue: number | null;
  /** Soldera's take: the commission fee, swap margin or fee the bonus is a cut of. */
  net_revenue: number | null;
  /** True when the bonus is a cut of the sale's margin rather than of Soldera's commission fee. */
  is_swap: boolean;
  status: BookedCommissionStatus;
  expense_invoice_id: number | null;
  expense_invoice_number: string | null;
}

export interface BookedReferredCompanyGroup {
  referred_company_id: number;
  referred_company_name: string | null;
  deals: BookedCommissionDeal[];
  total: number;
  paid: number;
  requested: number;
  pending: number;
  gross_revenue: number;
  net_revenue: number;
}

export interface BookedReferrerGroup {
  referrer_company_id: number;
  referrer_company_name: string | null;
  companies: BookedReferredCompanyGroup[];
  total: number;
  paid: number;
  requested: number;
  pending: number;
  gross_revenue: number;
  net_revenue: number;
}

export interface BookedCommissions {
  referrers: BookedReferrerGroup[];
  total_earned: number;
  paid: number;
  requested: number;
  pending: number;
  /** Counts a sale referred by several of the selected referrers once. */
  gross_revenue: number;
  net_revenue: number;
}

/**
 * The financial dashboard's figures for the business a partner referred.
 *
 * @property referral_cost - Referral bonuses booked on that business (predicted on open sales), any partner's
 * @property referred_companies_count - Referred companies with business in the period
 */
export interface SalesPartnerRevenueRow {
  referrer_company_id: number;
  referrer_company_name: string | null;
  sales_partner_type: string | null;
  referred_companies_count: number;
  sales_count: number;
  volume_mwh: number;
  gross_revenue: number;
  net_revenue: number;
  referral_cost: number;
  net_after_referral: number;
}

/**
 * Totals count a sale co-referred by several partners once, so they can be below the sum of the rows.
 *
 * @property dashboard_gross_revenue - The whole financial dashboard over the period, partnered or not, settled + pending;
 * null (as are the other dashboard_* fields) for a sales partner, who sees only their own business
 */
export interface SalesPartnerRevenue {
  date_from: string;
  date_to: string;
  partners: SalesPartnerRevenueRow[];
  gross_revenue: number;
  net_revenue: number;
  referral_cost: number;
  net_after_referral: number;
  dashboard_gross_revenue: number | null;
  dashboard_net_revenue: number | null;
  dashboard_net_after_referral: number | null;
}
